import { Filter, useSearchkit } from '@searchkit/client';
import React, { useCallback, type ReactNode } from 'react';
import type { search } from 'sr-types';
import SaveFilterModal from '../../components/SaveFilterModal';
import { useSavedFilters } from '../../hooks/useSavedFilters';
import type { SavePreferenceFormData } from '../../types/savePreference';
import { SavedFiltersContext } from './contexts/SavedFiltersContext';

type SavedFiltersProviderProps = {
    children: ReactNode;
    immutableFilters: Filter[];
    preferenceEntityKey: string;
    excludedFilterIdentifiers?: string[];
    facets?: search.SkFacetSet[];
};

const defaultSaveFilterInitialData: SavePreferenceFormData = {
    name: '',
    description: '',
    shared: false,
    pinAsTab: false
};

export function SavedFiltersProvider({
    preferenceEntityKey,
    immutableFilters,
    excludedFilterIdentifiers = [],
    facets = [],
    children
}: SavedFiltersProviderProps) {
    const api = useSearchkit();
    const savedFilters = useSavedFilters(preferenceEntityKey, api, {
        immutableFilters,
        excludedFilterIdentifiers
    });

    const {
        state: { saveFilterOpen, newTabSaveModalOpen, newTabSaveModalConfig, savingFilter, currentFilters },
        actions: { setSaveFilterOpen, closeNewTabSaveModal, saveFilter }
    } = savedFilters;

    const isSaveFilterModalOpen = saveFilterOpen || newTabSaveModalOpen;

    const handleCloseSaveFilterModal = useCallback(() => {
        setSaveFilterOpen(false);
        closeNewTabSaveModal();
    }, [setSaveFilterOpen, closeNewTabSaveModal]);

    const handleSaveFilterModal = useCallback(
        async (formData: SavePreferenceFormData) => {
            if (newTabSaveModalOpen && newTabSaveModalConfig) {
                await saveFilter(
                    formData,
                    undefined,
                    newTabSaveModalConfig.accountId,
                    newTabSaveModalConfig.preferenceId
                );
                return;
            }

            await saveFilter(formData);
        },
        [newTabSaveModalOpen, newTabSaveModalConfig, saveFilter]
    );

    const saveFilterModalInitialData =
        newTabSaveModalOpen && newTabSaveModalConfig ? newTabSaveModalConfig.initialData : defaultSaveFilterInitialData;

    const isNewTabEditSave = Boolean(newTabSaveModalOpen && newTabSaveModalConfig?.mode === 'edit');

    return (
        <SavedFiltersContext.Provider value={savedFilters}>
            {children}
            <SaveFilterModal
                open={isSaveFilterModalOpen}
                onClose={handleCloseSaveFilterModal}
                onSave={handleSaveFilterModal}
                appliedFilters={currentFilters}
                isLoading={savingFilter}
                facets={facets}
                isEdit={isNewTabEditSave}
                initialData={saveFilterModalInitialData}
            />
        </SavedFiltersContext.Provider>
    );
}
